import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthModel } from '@nousmedis/nm-guard-auth';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

export type ResetPasswordState = 'default' | 'loading' | 'success' | 'error';

@Component({
  selector: 'sw-password-reset-modal',
  templateUrl: './password-reset-modal.component.html',
  styleUrls: ['./password-reset-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordResetModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() email = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() resetSent = new EventEmitter<void>();

  form!: FormGroup;
  state: ResetPasswordState = 'default';

  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthModel);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['email'] && this.email) {
      this.form.get('email')?.setValue(this.email);
    }
    if (changes['visible'] && changes['visible'].currentValue === true) {
      this.state = 'default';
      if (!this.email) {
        this.form.reset();
      }
    }
  }

  get emailControl(): AbstractControl {
    return this.form.get('email')!;
  }

  get isLoading(): boolean {
    return this.state === 'loading';
  }

  get isSuccess(): boolean {
    return this.state === 'success';
  }

  onClose(): void {
    this.state = 'default';
    this.form.reset();
    this.visibleChange.emit(false);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const emailValue = this.emailControl.value as string;
    this.state = 'loading';
    this.cdr.markForCheck();

    try {
      await this.auth.resetPass(emailValue);
      this.state = 'success';
      this.resetSent.emit();

      this.translate
        .get([
          'USER.RESET_PASSWORD.SUCCESS_SUMMARY',
          'USER.RESET_PASSWORD.SUCCESS_DETAIL',
        ])
        .subscribe((translations) => {
          this.messageService.add({
            key: 'base-messages',
            severity: 'success',
            summary: translations['USER.RESET_PASSWORD.SUCCESS_SUMMARY'],
            detail: translations['USER.RESET_PASSWORD.SUCCESS_DETAIL'],
          });
        });

      setTimeout(() => {
        this.onClose();
      }, 3000);
    } catch {
      this.state = 'error';

      this.translate
        .get([
          'USER.RESET_PASSWORD.ERROR_SUMMARY',
          'USER.RESET_PASSWORD.ERROR_DETAIL',
        ])
        .subscribe((translations) => {
          this.messageService.add({
            key: 'base-messages',
            severity: 'error',
            summary: translations['USER.RESET_PASSWORD.ERROR_SUMMARY'],
            detail: translations['USER.RESET_PASSWORD.ERROR_DETAIL'],
          });
        });
    } finally {
      this.cdr.markForCheck();
    }
  }
}
